import type { ChangeEvent, ReactNode, RefObject } from "react";
import { SignatureModal } from "../../../components/Form/SignatureModel";
import type { PLOFormOneData } from "../../../types/PLOs/plo_one.type";


export interface AttachmentFile {
  name: string;
  [key: string]: any;
}
export interface DetailFormDocumentProps {
  formData: PLOFormOneData;
  isEditable: boolean;
  isSigning: boolean;
  isPreview:boolean;
  sigCanvasRef: RefObject<any>;
  setIsSigning: (isSigning: boolean) => void;
  
  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // Updated to accept MouseEvent
  handleClearSignature: (e?: React.MouseEvent) => void;
  handleSaveSignature: () => void;
  // Flexible string / keyof FormData support
  renderDocumentRow: (label: string, fieldName: any) => ReactNode;
  renderDatePicker: (fieldName: any) => ReactNode;

  // Action controls
  // handleBackNavigation: () => void;
  handleClearDate?: (fieldName: keyof PLOFormOneData) => void;
}
export const PloDetailFormOneDocument: React.FC<DetailFormDocumentProps> = ({
  formData,
  isEditable,
  isSigning,
  sigCanvasRef,
  setIsSigning,
  handleChange,
  handleClearSignature,
  handleSaveSignature,
  renderDocumentRow,
  renderDatePicker,
}) => {
  return (
    <div className="lg:col-span-8 bg-white p-6 md:p-10 rounded-xl border border-slate-200 shadow-sm select-text print:border-none print:shadow-none print:p-0">
      <div className="flex justify-end items-center mb-6">
        {/* <span className="text-xs text-slate-400">ID: {formData.id || 'N/A'}</span> */}
        <span className="text-xs font-semibold text-slate-600 tracking-wider">PTI ပုံစံ</span>
      </div>

      <div className="text-center mb-8">
        <h2 className="text-xl font-bold text-slate-900 leading-relaxed">
          အကြိုတွေ့ဆုံမေးမြန်းခြင်း ပြီးပြတ်ချက်ပုံစံ
        </h2>
      </div>

      <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
        {renderDocumentRow('၁။ တရားရုံးအမည်', 'courtName')}
        {renderDocumentRow('၂။ အမှုအမှတ်', 'caseNumber')}
        {renderDocumentRow('၃။ ရှေ့ပြေးဥပဒေရုံးအမည်', 'advocateOfficeName')}
        {renderDocumentRow('၄။ တရားလို / တရားခံအမည်', 'plaintiffOrAccused')}

        {/* Item 5 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-1.5">
          <label className="text-slate-800 font-medium text-sm sm:w-72 shrink-0">
            ၅။ ရဲစခန်းအမည်၊ ပထမသတင်းပေးတိုင်ချက်အမှတ်
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow w-full">
            <div className="flex items-center">
              {!isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-b border-dashed border-slate-400 min-h-[1.75rem] px-1 py-0.5">
                  {formData.policeStationInfoAndNumber || (
                    <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>
                  )}
                </span>
              ) : (
                <input
                  type="text"
                  name="policeStationInfoAndNumber"
                  value={formData.policeStationInfoAndNumber || ''}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium focus:border-slate-600"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('policeReportDate')}</div>
            </div>
          </div>
        </div>

        {/* Item 6 */}
        <div className="flex flex-col gap-2 py-1.5">
          <label className="text-slate-800 font-medium text-sm">
            ၆။ အမှုအကြောင်းအရာအကျဉ်းချုပ်
          </label>
          <div className="relative w-full">
            {!isEditable ? (
              <p className="text-sm text-slate-900 font-medium leading-[28px] whitespace-pre-wrap bg-transparent p-1.5 rounded min-h-[6rem] border-b border-dashed border-slate-400">
                {formData.caseSummary || (
                  <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>
                )}
              </p>
            ) : (
              <textarea
                name="caseSummary"
                rows={4}
                value={formData.caseSummary || ''}
                onChange={handleChange}
                className="w-full bg-transparent border-0 p-1.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium resize-none"
                style={{
                  backgroundImage:
                    'linear-gradient(transparent, transparent 27px, #cbd5e1 27px, #cbd5e1 28px)',
                  backgroundSize: '100% 28px',
                  lineHeight: '28px',
                }}
              />
            )}
          </div>
        </div>

        {/* Item 7 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-1.5">
          <label className="text-slate-800 font-medium text-sm sm:w-72 shrink-0">
            ၇။ တရားစွဲတင်ပို့သည့် ဥပဒေပုဒ်မ
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow w-full">
            <div className="flex items-center">
              {!isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-b border-dashed border-slate-400 min-h-[1.75rem] px-1 py-0.5">
                  {formData.chargeSection || (
                    <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>
                  )}
                </span>
              ) : (
                <input
                  type="text"
                  name="chargeSection"
                  value={formData.chargeSection || ''}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium focus:border-slate-600"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('chargeSectionDate')}</div>
            </div>
          </div>
        </div>

        {renderDocumentRow('၈။ သက်သေအမည် / အသက်', 'witnessDetails')}
        {renderDocumentRow('၉။ တွေ့ဆုံမေးမြန်းသူ (အမှုလိုက်ဥပဒေအရာရှိ)', 'interviewerDetails')}

        {/* Item 10 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-1.5">
          <label className="text-slate-800 font-medium text-sm sm:w-72 shrink-0">
            ၁၀။ အကြိုတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်ရက်စွဲ
          </label>
          <div className="flex-grow max-w-xs">{renderDatePicker('interviewDate')}</div>
        </div>

        {renderDocumentRow('၁၁။ အကြိုတွေ့ဆုံမေးမြန်းမှုပြုလုပ်သည့်နေရာ', 'interviewLocation')}
        {renderDocumentRow('၁၂။ အကူအညီပေးသည့် အဖွဲ့အစည်း', 'supportingOrganization')}
        {renderDocumentRow('၁၃။ တက်ရောက်လေ့လာသူ', 'observerName')}

        {/* Item 14 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-1.5">
          <label className="text-slate-800 font-medium text-sm sm:w-72 shrink-0">
            ၁၄။ တရားရုံးသက်သေခံသည့်ရက်စွဲ
          </label>
          <div className="flex-grow max-w-xs">{renderDatePicker('courtSummonsDate')}</div>
        </div>

        {/* Item 15 */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-1.5">
          <label className="text-slate-800 font-medium text-sm sm:w-72 shrink-0">
            ၁၅။ အပြီးသတ်အမိန့်ချသည့် အမိန့်
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-grow w-full">
            <div className="flex items-center">
              {!isEditable ? (
                <span className="text-sm font-medium text-slate-900 block w-full border-b border-dashed border-slate-400 min-h-[1.75rem] px-1 py-0.5">
                  {formData.finalOrderDetails || (
                    <span className="text-slate-300 italic font-normal">(ကွက်လပ်)</span>
                  )}
                </span>
              ) : (
                <input
                  type="text"
                  name="finalOrderDetails"
                  value={formData.finalOrderDetails || ''}
                  onChange={handleChange}
                  className="w-full bg-transparent border-0 border-b border-dashed border-slate-400 p-0 pb-0.5 text-sm text-slate-900 focus:outline-none focus:ring-0 font-medium focus:border-slate-600"
                />
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium shrink-0">ရက်စွဲ -</span>
              <div className="flex-grow">{renderDatePicker('finalOrderDate')}</div>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="pt-10 flex flex-col items-end gap-1.5 text-sm text-slate-800 pr-2">
          <div className="w-72 space-y-3">
            <div className="flex items-center">
              <span className="w-24 text-right text-xs sm:text-sm font-medium whitespace-nowrap mr-3">
                လက်မှတ်
              </span>
              {!isEditable ? (
                <div className="flex-1 min-h-[36px] flex items-center justify-center border-b border-dashed border-slate-400">
                  {formData.signatureImage ? (
                    <img
                      src={formData.signatureImage}
                      alt="Signature"
                      className="h-8 object-contain mix-blend-multiply"
                    />
                  ) : (
                    <span className="text-slate-300 italic text-xs">(လက်မှတ်မရှိပါ)</span>
                  )}
                </div>
              ) : (
                <div className="flex-1 relative flex items-center">
                  <div
                    onClick={() => setIsSigning(true)}
                    className="w-full bg-transparent border-b border-dashed border-slate-400 min-h-[36px] flex items-center justify-center cursor-pointer hover:bg-slate-50 transition relative group rounded-t"
                  >
                    {formData.signatureImage ? (
                      <>
                        <img
                          src={formData.signatureImage}
                          alt="Signature"
                          className="h-8 object-contain mix-blend-multiply"
                        />
                        <span className="absolute inset-0 bg-slate-900/60 text-white text-[11px] opacity-0 group-hover:opacity-100 flex items-center justify-center transition rounded-t print:hidden">
                          လက်မှတ်အသစ်ထိုးရန်
                        </span>
                      </>
                    ) : (
                      <span className="text-slate-400 text-xs print:hidden flex items-center gap-1">
                        ✍️ လက်မှတ်ထိုးရန် နှိပ်ပါ
                      </span>
                    )}
                  </div>
                  {formData.signatureImage && (
                    <button
                      type="button"
                      onClick={handleClearSignature}
                      title="လက်မှတ်ရှင်းလင်းမည်"
                      className="absolute -right-6 text-slate-400 hover:text-rose-500 text-xs px-1 font-bold z-10 print:hidden"
                    >
                      ✕
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <span className="w-24 text-right text-xs sm:text-sm font-medium whitespace-nowrap mr-3">
                အမည်
              </span>
              {!isEditable ? (
                <span className="flex-1 text-center font-medium border-b border-dashed border-slate-400 min-h-[28px] py-0.5">
                  {formData.signName || '-'}
                </span>
              ) : (
                <input
                  type="text"
                  name="signName"
                  value={formData.signName || ''}
                  onChange={handleChange}
                  className="flex-1 bg-transparent border-0 border-b border-dashed border-slate-400 focus:outline-none focus:ring-0 text-center p-0 pb-0.5 text-sm font-medium text-slate-900 focus:border-slate-600"
                />
              )}
            </div>

            <p className="font-semibold text-center text-slate-900 pt-1 text-sm">တွေ့ဆုံမေးမြန်းသူ</p>

            <div className="flex items-center">
              <span className="w-24 text-right text-xs sm:text-sm font-medium whitespace-nowrap mr-3">
                တင်ပြသည့်ရက်စွဲ
              </span>
              <div className="flex-1">{renderDatePicker('submitDate')}</div>
            </div>
          </div>
        </div>

      </form>

      {/* Signature Canvas Dialog */}
      {isSigning && isEditable && (
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => setIsSigning(false)}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleSaveSignature}
        />
      )}
    </div>
  );
};
